import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import Tooltip from "components/Tooltip";
import useUserSettings from "features/User/hooks/useUserSettings";
import { getLibraryById } from "helpers/api";
import useLabels from "hooks/useLabels";
import { Landmark } from "lucide-react";
import { useEffect, useState } from "react";

type LibraryIconProps = {
  size?: number;
  className?: string;
  fallbackIcon?: string;
  includeTooltip?: boolean;
  includePadding?: boolean;
};

export const LibraryIcon = ({
  size = 16,
  className,
  includeTooltip = true,
  includePadding = true,
}: LibraryIconProps) => {
  const { library_id } = useUserSettings();
  const [imageError, setImageError] = useState(false);
  const [tooltipsLabels] = useLabels("tooltips");

  // Fetch library data if user has a library_id
  const { data: library, isLoading } = useQuery({
    queryKey: ["library", library_id],
    queryFn: () => getLibraryById(library_id!),
    enabled: !!library_id,
    staleTime: Infinity,
  });

  // Reset image error when library changes
  useEffect(() => {
    setImageError(false);
  }, [library?.library_id]);

  const altText = library?.library_name;
  const iconSrc = library?.logo;

  const iconElement =
    iconSrc && !imageError && !isLoading ? (
      <img
        src={iconSrc}
        alt={altText}
        className={classNames(
          size <= 16 ? "rounded-sm" : "rounded-lg",
          "object-contain shrink-0",
          className
        )}
        style={{
          width: size,
          height: size,
        }}
        onError={() => setImageError(true)}
      />
    ) : (
      <Landmark size={size} className={classNames("shrink-0", className)} />
    );

  if (includeTooltip) {
    return (
      <div className="relative p-1">
        <Tooltip
          content={
            <div className="label">{tooltipsLabels["institution-access"]}</div>
          }
          placement="top"
        >
          {iconElement}
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        size <= 16 ? "rounded-sm" : "rounded-lg",
        "relative bg-white overflow-hidden flex items-center justify-center",
        includePadding && "p-1"
      )}
    >
      {iconElement}
    </div>
  );
};
